// Resolves @-reference strings found in node paramValues into their runtime values,
// mirroring api/src/services/workflowExecutionService.js → resolveAtVariables.
//
// Grammar:
//   "@VarName"                 → vars[VarName]
//   "@NodeTitle"               → last output of node titled NodeTitle
//   "@NodeTitle.path.to.field" → deep property on that output
//   "@VarName++" / "@VarName--"→ current variable value (we do NOT mutate here — preview only)
//
// Non-@ values are returned as-is. Unresolvable @-refs return the original string so
// the AI can still see the raw reference.

var { minimizeArrays, minimizeStringBase64 } = require('./apiClient');

function extractPropPath(value, path) {
  if (value == null || !path) return value;
  var parts = String(path).split('.').filter(Boolean);
  var cur = value;
  for (var i = 0; i < parts.length; i++) {
    if (cur == null) return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}

function lookupName(name, vars, nodeOutputByTitle) {
  if (vars && Object.prototype.hasOwnProperty.call(vars, name)) {
    return { found: true, value: vars[name] };
  }
  if (nodeOutputByTitle && Object.prototype.hasOwnProperty.call(nodeOutputByTitle, name)) {
    return { found: true, value: nodeOutputByTitle[name] };
  }
  return { found: false };
}

function resolveAtRef(rawValue, vars, nodeOutputByTitle) {
  if (typeof rawValue !== 'string') return rawValue;
  var trimmed = rawValue.trim();
  if (trimmed.length <= 1 || trimmed.charAt(0) !== '@') return rawValue;

  var name = trimmed.slice(1).trim();
  if (!name) return rawValue;

  // Strip ++/-- suffix — we don't mutate, just show current value.
  var suffixMatch = name.match(/^(.+?)(\+\+|--)$/);
  if (suffixMatch) name = suffixMatch[1].trim();

  var direct = lookupName(name, vars, nodeOutputByTitle);
  if (direct.found) return direct.value;

  var dotIdx = name.indexOf('.');
  if (dotIdx > 0 && dotIdx < name.length - 1) {
    var baseName = name.slice(0, dotIdx).trim();
    var propPath = name.slice(dotIdx + 1).trim();
    if (baseName && propPath) {
      var baseHit = lookupName(baseName, vars, nodeOutputByTitle);
      if (baseHit.found) return extractPropPath(baseHit.value, propPath);
    }
  }
  return rawValue;
}

function minimizeForPrompt(value) {
  if (typeof value === 'string') return minimizeStringBase64(value);
  if (value !== null && typeof value === 'object') return minimizeArrays(value);
  return value;
}

// Resolves every value in a paramValues object. Returns a new { key: resolvedValue } map
// with each value already passed through minimizeArrays / minimizeStringBase64 so the
// prompt stays compact (large arrays and base64 blobs are trimmed).
function resolveParamValues(paramValues, vars, nodeOutputByTitle) {
  var out = {};
  if (!paramValues || typeof paramValues !== 'object') return out;
  var keys = Object.keys(paramValues);
  for (var i = 0; i < keys.length; i++) {
    var k = keys[i];
    var resolved = resolveAtRef(paramValues[k], vars, nodeOutputByTitle);
    out[k] = minimizeForPrompt(resolved);
  }
  return out;
}

module.exports = {
  resolveAtRef: resolveAtRef,
  resolveParamValues: resolveParamValues
};
